import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { OAuthService } from "angular-oauth2-oidc";
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";
import { ApiRequest } from "src/app/_models/common/request-response/api-request";
import { Claims } from "src/app/_models/common/claims";
import { UserTypeEnum } from "src/app/_models";
import { IPliantConnectionCallbackRequest } from "src/app/_models/credit-card/pliant-connection";
import { ClaimsService } from "src/app/_services/common/claims.service";
import { CreditCardService } from "src/app/_services/credit-card/credit-card.service";
import { environment } from "src/environments/environment";
import { LoginNewComponent } from "../onboarding/login-new/login-new.component";
import { OnboardingBannerComponent } from "../onboarding/onboarding-banner/onboarding-banner.component";

// sessionStorage key holding Pliant's redirect parameters while the admin logs
// in; login-new's routeUser() checks it to return the user to this page.
export const PLIANT_CONNECT_PARAMS_KEY = "pliantConnectParams";

type PliantConnectState = "login" | "connecting" | "success" | "error" | "notAllowed" | "invalid";

// Landing page registered with Pliant: their web app redirects the customer
// admin here (with organizationId/organizationName/partnerId/redirectUrl/t/v1a
// query params) to log into EOD and complete the connection.
@Component({
    selector: "app-pliant-connect",
    templateUrl: "./pliant-connect.component.html",
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        LoginNewComponent,
        OnboardingBannerComponent
    ]
})
export class PliantConnectComponent implements OnInit, OnDestroy {
  state: PliantConnectState = "connecting";
  organizationName: string = "";
  returnUrl: string = "";
  redirectCountdown: number = 0;

  private pliantParams: IPliantConnectionCallbackRequest | null = null;
  private completing = false;
  private countdownTimer: any;
  private destroy$ = new Subject<void>();
  claims: Claims;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private oauthService: OAuthService,
    private claimsService: ClaimsService,
    private creditCardService: CreditCardService
  ) {
    this.claimsService.currentClaims.subscribe(
      (claims) => (this.claims = claims)
    );
  }

  ngOnInit(): void {
    // Re-evaluate on every navigation to this route - the initial arrival from
    // Pliant and the post-login resume both land here.
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.evaluate());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  returnToPliant(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    if (this.returnUrl) {
      window.location.href = this.returnUrl;
    }
  }

  private evaluate(): void {
    if (this.completing) {
      return;
    }

    this.capturePliantParamsFromUrl();

    const stored = sessionStorage.getItem(PLIANT_CONNECT_PARAMS_KEY);
    if (!stored) {
      this.state = "invalid";
      return;
    }

    this.pliantParams = JSON.parse(stored);
    this.organizationName = this.pliantParams.organizationName;

    if (!this.isAuthenticated()) {
      this.state = "login";
      return;
    }

    // ClaimsService only holds claims in memory; on a fresh page load they are
    // normally rehydrated from localStorage by NavMenuComponent's constructor,
    // which lives inside the authenticated LayoutComponent shell. This route is
    // outside the layout (like /login), so an already-logged-in user arriving
    // here directly would see null claims unless we rehydrate the same way.
    if (
      !this.claims &&
      localStorage.getItem("claims") &&
      localStorage.getItem("claims") !== "undefined"
    ) {
      this.claimsService.updateClaims(JSON.parse(localStorage.getItem("claims")));
    }

    this.claimsService.currentClaims
      .pipe(take(1))
      .subscribe((claims: Claims) => {
        if (!claims) {
          this.state = "login";
          return;
        }

        const isFinanceManager =
          claims.IsFinanceManager == true ||
          claims.orgRoleId == UserTypeEnum.FinanceManager;
        if (!isFinanceManager) {
          this.state = "notAllowed";
          this.returnUrl = this.buildReturnUrl("error");
          sessionStorage.removeItem(PLIANT_CONNECT_PARAMS_KEY);
          return;
        }

        this.completeConnection();
      });
  }

  private capturePliantParamsFromUrl(): void {
    const queryParamMap = this.route.snapshot.queryParamMap;
    if (!queryParamMap.get("organizationId")) {
      return;
    }

    const params: IPliantConnectionCallbackRequest = {
      organizationId: queryParamMap.get("organizationId"),
      organizationName: queryParamMap.get("organizationName") || "",
      partnerId: queryParamMap.get("partnerId") || "",
      redirectUrl: queryParamMap.get("redirectUrl") || "",
      timestamp: Number(queryParamMap.get("t")) || 0,
      signature: queryParamMap.get("v1a") || "",
    };
    sessionStorage.setItem(PLIANT_CONNECT_PARAMS_KEY, JSON.stringify(params));
  }

  private completeConnection(): void {
    this.completing = true;
    this.state = "connecting";

    const request = new ApiRequest<IPliantConnectionCallbackRequest>(
      "CompletePliantConnection",
      environment.applicationVersion,
      environment.requestId
    );
    request.requestInfo = this.pliantParams;

    this.creditCardService
      .completePliantConnection(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          sessionStorage.removeItem(PLIANT_CONNECT_PARAMS_KEY);
          if (response.statusCode == 0 && response.responseInfo?.isConnected) {
            this.state = "success";
            this.returnUrl = response.responseInfo.redirectUrl;
          } else {
            this.state = "error";
            this.returnUrl =
              response.responseInfo?.redirectUrl || this.buildReturnUrl("error");
          }
          this.scheduleReturnToPliant();
        },
        () => {
          sessionStorage.removeItem(PLIANT_CONNECT_PARAMS_KEY);
          this.state = "error";
          this.returnUrl = this.buildReturnUrl("error");
          this.scheduleReturnToPliant();
        }
      );
  }

  private scheduleReturnToPliant(): void {
    if (!this.returnUrl) {
      return;
    }
    this.redirectCountdown = 5;
    this.countdownTimer = setInterval(() => {
      this.redirectCountdown--;
      if (this.redirectCountdown <= 0) {
        this.returnToPliant();
      }
    }, 1000);
  }

  private buildReturnUrl(status: string): string {
    const redirectUrl = this.pliantParams?.redirectUrl;
    if (!redirectUrl) {
      return "";
    }
    return redirectUrl.includes("?")
      ? `${redirectUrl}&status=${status}`
      : `${redirectUrl}?status=${status}`;
  }

  private isAuthenticated(): boolean {
    return (
      this.oauthService.hasValidAccessToken() &&
      this.oauthService.getAccessTokenExpiration() > new Date().getTime()
    );
  }
}
